/** The pure part of the Reports screen: which tab a filing is on, and where each filing stands.
 *
 * **Nothing here decides anything the server did not.** The verdict, the blocking count and the
 * date are the server's; this file words them for a row. The one rule worth keeping in view is the
 * ladder's: `checks_passed` is *not* approval (F07), so it is drawn with the informational tone and
 * its next step is "get it approved", never a green tick. Only `approved` and `issued` — which have
 * a person behind them — take the positive tone.
 *
 * **A read that has not answered is a state, not a pass.** `reading` and `failed` have their own
 * words and are counted as neither blocked nor ready.
 */

import type { Obligation, Report } from "../api/queries";
import type { StatusTone } from "../components/StatusPill";
import { dueStatus } from "./dueStatus";
import { daysBetween } from "./readinessModel";
import { readVerdict, summariseFindings } from "./standingModel";

/** The six views of one filing (journey 4). In the URL as `tab`; absent means Disclosures. */
export const FILING_TABS = [
  { key: "disclosures", label: "Disclosures" },
  { key: "checks", label: "Checks" },
  { key: "checklist", label: "Checklist" },
  { key: "findings", label: "Findings" },
  { key: "bridge", label: "Year-on-year bridge" },
  { key: "issue", label: "Issue" },
  { key: "submission", label: "Submission pack" },
] as const;

export type FilingTab = (typeof FILING_TABS)[number]["key"];

/** A tab name from a URL: one of the six, else the default. A value nothing matches (the register's
 * `tab=register` carried across screens, or a typo) opens Disclosures rather than a blank panel. */
export function filingTabOf(value: unknown): FilingTab {
  const found = FILING_TABS.find((entry) => entry.key === value);
  return found ? found.key : "disclosures";
}

/** Whether `tab` names one of a filing's long views. With none, a filing opens as its page (the
 * percentage, the path and the to-do list); a tab nothing matches opens that page too, so a link
 * carrying another screen's `tab=` lands on the page rather than a panel it never asked for. */
export function isDetailTab(value: unknown): boolean {
  return FILING_TABS.some((entry) => entry.key === value);
}

/** What the screen knows about one filing's report. */
export type FilingRead =
  | { state: "not_asked" }
  | { state: "reading" }
  | { state: "failed" }
  | { state: "ready"; report: Report };

/** The groups the strip above the list counts. A filing is in exactly one. */
export type Group = "not_owed" | "unread" | "blocked" | "ready" | "issued";

export interface Standing {
  group: Group;
  tone: StatusTone;
  /** The pill's words. */
  label: string;
  /** One sentence naming the next step, derived from the verdict and the blocking count only. */
  next: string;
  blocking: number;
  advisory: number;
}

/** Whether a filing is owed: required, and the determination is not waiting on a missing fact. */
export function isOwed(filing: Obligation): boolean {
  return filing.status === "required" && !filing.undetermined;
}

/** The words for a determination that is not a requirement to file. */
function notOwedWords(filing: Obligation): string {
  if (filing.undetermined) return "Not determined";
  switch (filing.status) {
    case "exempt":
      return "Exempt";
    case "not_in_scope":
      return "Not in scope";
    case "not_required":
      return "Not required";
    case "satisfied_by_parent":
      return "Filed by a parent";
    default:
      return filing.status.replace(/_/g, " ");
  }
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function standingOf(filing: Obligation, read: FilingRead): Standing {
  if (!isOwed(filing)) {
    return {
      group: "not_owed",
      tone: "neutral",
      label: notOwedWords(filing),
      next: filing.undetermined
        ? "Supply the facts this determination is missing"
        : "Nothing to file",
      blocking: 0,
      advisory: 0,
    };
  }
  if (read.state === "reading" || read.state === "not_asked") {
    return {
      group: "unread",
      tone: "neutral",
      label: "Reading",
      next: "Reading this filing's checks",
      blocking: 0,
      advisory: 0,
    };
  }
  if (read.state === "failed") {
    return {
      group: "unread",
      tone: "warn",
      label: "Not read",
      next: "Open the filing to try again",
      blocking: 0,
      advisory: 0,
    };
  }
  const { report } = read;
  const findings = summariseFindings(report.findings);
  const counts = { blocking: findings.blocking, advisory: findings.advisory };
  const verdict = readVerdict(report.verdict);
  if (!verdict.known) {
    return {
      group: "unread",
      tone: "warn",
      label: "Unrecognised state",
      next: "Open the filing: this screen does not know that state",
      ...counts,
    };
  }
  switch (report.verdict) {
    case "issued":
      return {
        group: "issued",
        tone: "ok",
        label: "Issued",
        next: "None. A filing is on record",
        ...counts,
      };
    case "approved":
      return {
        group: "ready",
        tone: "ok",
        label: "Approved",
        next: "Issue the filing",
        ...counts,
      };
    case "checks_passed":
      return {
        group: "ready",
        tone: "info",
        label: "Checks passed",
        next: "Have it approved, then issue it",
        ...counts,
      };
    case "incomplete":
      return {
        group: "blocked",
        tone: "crit",
        label: "Not ready",
        next:
          findings.blocking > 0
            ? `Clear ${plural(findings.blocking, "blocking finding", "blocking findings")}`
            : "Read why it is not ready",
        ...counts,
      };
    case "unsupported":
      return {
        group: "blocked",
        tone: "warn",
        label: "Not supported",
        next: "Read what EmitIQ cannot produce here",
        ...counts,
      };
    default:
      return {
        group: "blocked",
        tone: "warn",
        label: "Not checked",
        next: "Read why it has not been checked",
        ...counts,
      };
  }
}

export interface Strip {
  owed: number;
  blocked: number;
  ready: number;
  issued: number;
  unread: number;
  /** The earliest date among owed filings that are not yet issued; `undefined` when none has one. */
  nextDue: { date: string; basis: string; regime: string; entity: string } | undefined;
}

export function stripOf(
  filings: readonly Obligation[],
  reads: Readonly<Record<string, FilingRead>>,
): Strip {
  const strip: Strip = { owed: 0, blocked: 0, ready: 0, issued: 0, unread: 0, nextDue: undefined };
  for (const filing of filings) {
    if (!isOwed(filing)) continue;
    strip.owed += 1;
    const standing = standingOf(filing, reads[filing.obligation_id] ?? { state: "not_asked" });
    if (standing.group === "blocked") strip.blocked += 1;
    else if (standing.group === "ready") strip.ready += 1;
    else if (standing.group === "issued") strip.issued += 1;
    else strip.unread += 1;
    // A filing that is already issued has nothing left to be late for. A date of unknown standing
    // (an unread report) still counts: hiding it would make the next deadline look later than it is.
    if (standing.group !== "issued" && filing.due_date) {
      if (!strip.nextDue || filing.due_date < strip.nextDue.date) {
        strip.nextDue = {
          date: filing.due_date,
          basis: filing.due_date_basis,
          regime: filing.regime_key.replace(/_/g, " "),
          entity: filing.legal_entity_name,
        };
      }
    }
  }
  return strip;
}

/** Owed filings first; within them **live law before a regime that is only proposed or enjoined**
 * (ADR-0199: a filing under an enjoined regime never leads a customer with a live duty), then the
 * soonest date, undated last, then by company. A list that opens on a determination of "not in
 * scope" is a list that opens on nothing to do, and one that opens on a date nobody is bound to is
 * barely better. Which regime is live is **the server's** (`in_force_caveat`), never a key listed
 * here: the day a market is added, this order needs no edit. */
export function orderFilings(filings: readonly Obligation[]): Obligation[] {
  const rank = (filing: Obligation) => (filing.status === "required" ? 0 : 1);
  const notLaw = (filing: Obligation) => (filing.in_force_caveat || filing.in_force_label ? 1 : 0);
  return [...filings].sort((a, b) => {
    const owed = rank(a) - rank(b);
    if (owed) return owed;
    if (rank(a) === 0) {
      const law = notLaw(a) - notLaw(b);
      if (law) return law;
      if (a.due_date !== b.due_date) {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date.localeCompare(b.due_date);
      }
    }
    return a.legal_entity_name.localeCompare(b.legal_entity_name);
  });
}

export type DueTone = "crit" | "warn" | "ok" | "neutral";

export interface Due {
  /** Whole days from today to the date; `undefined` when there is no date or no countdown. */
  days: number | undefined;
  tone: DueTone;
  /** A few words: `9 days left`, `31 days overdue`, `Date proposed`, `Issued`. */
  words: string;
}

/** Days within which a filing that is not done is drawn as due soon. A presentation threshold for
 * the colour of a mark, not a regulatory fact: the words always carry the number of days. */
export const DUE_SOON_DAYS = 30;

/** When a filing is due, in words and a tone. **A date in a regime that is not yet law is not
 * counted down to** (C-03, C-07): it says the date is proposed and has no countdown, because a
 * countdown is a claim that a duty is bearing down. An issued filing has nothing left to be late
 * for, so it says so and is never crit. */
export function dueOf(filing: Obligation, group: Group, today: string): Due {
  if (group === "issued") return { days: undefined, tone: "ok", words: "Issued" };
  if (!filing.due_date) return { days: undefined, tone: "neutral", words: "No date set" };
  if (filing.in_force_caveat || filing.in_force_label) {
    return { days: undefined, tone: "neutral", words: "Date proposed" };
  }
  const days = daysBetween(today, filing.due_date);
  if (days === undefined) return { days: undefined, tone: "neutral", words: "No date set" };
  if (days < 0) {
    return { days, tone: "crit", words: dueStatus(days).overdue ?? "" };
  }
  if (days === 0) return { days, tone: "warn", words: "Due today" };
  return {
    days,
    tone: days <= DUE_SOON_DAYS ? "warn" : "neutral",
    words: `${days} ${days === 1 ? "day" : "days"} left`,
  };
}

/** Whether there is anything to choose between for this filing (ADR-0224): an included company the
 * figures leave out, or a choice already made - so a filing that consolidates and leaves nobody out
 * can still be changed back. */
export function hasBoundaryChoice(report: Report): boolean {
  const context = report.context;
  return (
    context.consolidation_chosen === "subsidiaries" ||
    context.consolidation === "subsidiaries" ||
    context.unconsolidated.some((member) => member.included)
  );
}

/** Whether anything here has something to say, so the panel is not drawn empty. */
export function hasQualifiers(report: Report, role: string | undefined): boolean {
  return Boolean(
    hasBoundaryChoice(report) ||
      report.context.clamped_by_engagement ||
      Number(report.unevidenced_tonnes) > 0 ||
      role === "auditor" ||
      report.context.base_year_notice ||
      report.context.unconsolidated.length ||
      report.data_quality_note,
  );
}
